import ReactEChartsCore from '../EChart';
import * as echarts from 'echarts/core';
import {
  TooltipComponent,
  LegendComponent,
  GridComponent
} from 'echarts/components';
import { BarChart } from 'echarts/charts';
import { CanvasRenderer } from 'echarts/renderers';
import { Spinner, Alert } from 'react-bootstrap';
import { useAppContext } from '../../../providers/AppProvider';
import { useSalesProductSummary } from '../../../features/orders/useSalesDashboard';

echarts.use([
  TooltipComponent,
  LegendComponent,
  GridComponent,
  BarChart,
  CanvasRenderer
]);

const fmt = v =>
  Number(v || 0).toLocaleString('ro-RO', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  });

const getChartOptions = (getThemeColor, data) => {
  const labels = data.map(d => d.product);

  return {
    color: [
      getThemeColor('primary'),
      getThemeColor('info'),
      getThemeColor('success'),
      getThemeColor('warning')
    ],
    tooltip: {
      trigger: 'axis',
      padding: [7, 10],
      backgroundColor: getThemeColor('gray-100'),
      borderColor: getThemeColor('gray-300'),
      textStyle: { color: getThemeColor('dark') },
      borderWidth: 1,
      transitionDuration: 0,
      axisPointer: { type: 'shadow' },
      formatter: params => {
        const rows = Array.isArray(params) ? params : [params];
        if (!rows.length) return '';
        const title = rows[0]?.axisValue ?? '';
        const body = rows
          .map(
            item =>
              `${item.marker} ${item.seriesName}: <strong>${fmt(
                item.value
              )} m</strong>`
          )
          .join('<br/>');
        return `<strong>${title}</strong><br/>${body}`;
      }
    },
    legend: {
      data: ['Ordered', 'Allocated', 'Produced', 'Remaining'],
      right: 'right',
      width: '100%',
      itemWidth: 16,
      itemHeight: 8,
      itemGap: 20,
      top: 3,
      inactiveColor: getThemeColor('gray-500'),
      textStyle: {
        color: getThemeColor('gray-900'),
        fontWeight: 600,
        fontFamily: 'Nunito Sans'
      }
    },
    xAxis: {
      type: 'category',
      data: labels,
      axisLabel: {
        color: getThemeColor('gray-800'),
        interval: 0,
        rotate: labels.length > 5 ? 25 : 0,
        fontFamily: 'Nunito Sans',
        fontWeight: 600,
        fontSize: 11
      },
      axisLine: { lineStyle: { color: getThemeColor('gray-300') } },
      axisTick: false
    },
    yAxis: {
      type: 'value',
      name: 'meters',
      nameTextStyle: {
        color: getThemeColor('gray-700'),
        fontFamily: 'Nunito Sans',
        fontWeight: 600
      },
      splitLine: { lineStyle: { color: getThemeColor('gray-200') } },
      axisLine: { show: false },
      axisLabel: {
        fontFamily: 'Nunito Sans',
        fontWeight: 600,
        fontSize: 12,
        color: getThemeColor('gray-800'),
        formatter: v => fmt(v)
      }
    },
    series: [
      {
        name: 'Ordered',
        type: 'bar',
        barWidth: '14px',
        data: data.map(d => d.ordered),
        itemStyle: {
          borderRadius: [2, 2, 0, 0],
          color: getThemeColor('primary')
        }
      },
      {
        name: 'Allocated',
        type: 'bar',
        barWidth: '14px',
        data: data.map(d => d.allocated),
        itemStyle: {
          borderRadius: [2, 2, 0, 0],
          color: getThemeColor('info')
        }
      },
      {
        name: 'Produced',
        type: 'bar',
        barWidth: '14px',
        data: data.map(d => d.produced),
        itemStyle: {
          borderRadius: [2, 2, 0, 0],
          color: getThemeColor('success')
        }
      },
      {
        name: 'Remaining',
        type: 'bar',
        barWidth: '14px',
        data: data.map(d => d.remaining),
        itemStyle: {
          borderRadius: [2, 2, 0, 0],
          color: getThemeColor('warning')
        }
      }
    ],
    grid: {
      right: 10,
      left: 10,
      bottom: 10,
      top: '15%',
      containLabel: true
    },
    animation: true
  };
};

const SalesProductBarChart = ({ height = '400px', width = '100%' }) => {
  const { getThemeColor } = useAppContext();
  const { data, isLoading, isError, error } = useSalesProductSummary();

  const chartData = data?.summary || [];

  if (isLoading) {
    return (
      <div
        style={{ height, width }}
        className="d-flex align-items-center justify-content-center"
      >
        <Spinner animation="border" size="sm" className="me-2" />
        <span>Loading chart...</span>
      </div>
    );
  }

  if (isError) {
    return (
      <Alert variant="danger" className="mb-0">
        Failed to load product summary: {error?.message || 'Unknown error'}
      </Alert>
    );
  }

  if (!chartData.length) {
    return (
      <div
        style={{ height, width }}
        className="d-flex align-items-center justify-content-center text-700"
      >
        No product data found
      </div>
    );
  }

  return (
    <ReactEChartsCore
      echarts={echarts}
      option={getChartOptions(getThemeColor, chartData)}
      style={{ height, width }}
      notMerge={true}
      lazyUpdate={true}
    />
  );
};

export default SalesProductBarChart;
